/**
 * auditFeed.ts — the change-history strip's reads and joins as a hook, shared
 * by SalesAuditStrip and the account page's merged timeline (Account detail
 * v3 AD1, design note §45). Moved out of SalesAuditStrip.tsx unchanged.
 */
import { useAsync, fetchFieldHistory, fetchDealStageEvents, fetchAccounts, fetchRetiredAccounts, fetchDirectory } from '../../api'
import { composeAudit, type AuditJoins } from './auditStrip'

/** The strip's reads and joins (Account detail v3 AD1 shares them with the
 *  account timeline): the composed rows, the id joins, and whether a read is
 *  still loading or failed. */
export function useAuditFeed(recordType: 'deals' | 'contacts' | 'accounts', recordId: string, withStageEvents = false) {
  const historyQ = useAsync(() => fetchFieldHistory(recordType, recordId), [recordType, recordId])
  const stageQ = useAsync(
    () => (withStageEvents ? fetchDealStageEvents(recordId) : Promise.resolve([])),
    [recordId, withStageEvents])

  // Join targets for the two id-valued fields. Both are best-effort: a failed
  // roster read degrades the strip to raw ids, it never blanks a row or
  // hides the change.
  const employeesQ = useAsync(() => fetchDirectory(), [])
  const accountsQ = useAsync(() => fetchAccounts(), [])
  // S6 ruling 2: a retired (merged) account is in no live read; its name and
  // its survivor come from their own read. A failure (an older api) leaves the
  // "not on file" wording — never a guess.
  const retiredQ = useAsync(() => fetchRetiredAccounts().catch(() => []), [])
  const joins: AuditJoins = {
    employeeName: (id) => (employeesQ.data ?? []).find(e => e.id === id)?.fullName,
    accountName: (id) => {
      const live = (accountsQ.data ?? []).find(a => a.id === id)
      if (live) return live.name
      const retired = (retiredQ.data ?? []).find(a => a.id === id)
      return retired ? `${retired.name} (merged into ${retired.mergedIntoName})` : undefined
    },
  }

  const loading = historyQ.loading || (withStageEvents && stageQ.loading)
  const rows = composeAudit(historyQ.data ?? [], stageQ.data ?? [])

  // A failed history read is NOT an empty history — saying "no changes" over
  // a 500 would state something we do not know.
  const failed = historyQ.error || (withStageEvents && stageQ.error)
  return { rows, joins, loading, failed: Boolean(failed) }
}
